"use client";
import { jsx } from "react/jsx-runtime";
import { useAgent } from "../../../utils/agent/AgentProvider.js";
import { ModuleSettingsStore } from "../../../utils/contentstack/moduleSettingsStore.js";
import { isNullOrEmpty } from "../../../utils/index.js";
import { useStwContext } from "../../../utils/stwInfo/hook.js";
import { AKAMAI_ZIP_CODE_LOCALE_VALIDATION_CONFIGURATION } from "../../../utils/validations/akamaiZipCode.js";
import { getWarehouseOptionsDetails, getWarehouseFilterParams } from "../../../utils/warehouses/findAWarehouse.lib.js";
import { RESULTS_ANNOUNCEMENT_KEY } from "../../../utils/warehouses/warehouseDrawer.constants.js";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import "../../Chrome/BrowseContext/ContextProvider.js";
import { useBrowseContext } from "../../Chrome/BrowseContext/hook.js";
import { getPreferredWarehouse, getSortedWarehouses, getCountryFromLocale } from "../utils/warehouse.lib.js";
import { useLocation } from "../../../hooks/useLocation/index.js";
import { useUpdatePreferredWarehouse } from "../../../hooks/useUpdatePreferredWarehouse/index.js";
import { WarehouseDrawerUI } from "./WarehouseDrawerUI/index.js";
const COMPONENT_NAME = "WarehouseDrawer";
const WarehouseDrawer = (props) => {
  var _a, _b;
  const {
    isOpen,
    locale,
    moduleSettings,
    setWarehouses,
    onClose,
    drawerType
  } = props;
  const { warehouse } = useBrowseContext();
  const { stwPostalCode } = useStwContext();
  const { agent } = useAgent();
  const updatePreferredWarehouse = useUpdatePreferredWarehouse();
  const [state, setState] = useState({
    announcement: "",
    isLoading: true,
    preferredWarehouse: void 0,
    preferredWarehouseData: void 0,
    warehouses: []
  });
  const [stwErrorMessage, setStwErrorMessage] = useState(false);
  const msStore = useMemo(
    () => new ModuleSettingsStore({
      composable_window: moduleSettings.composable_window,
      key_value: moduleSettings.key_value
    }),
    [moduleSettings]
  );
  const drawerOptionsDetails = useMemo(
    () => getWarehouseOptionsDetails(moduleSettings, false),
    [moduleSettings]
  );
  const resultsAnnouncement = msStore.getKeyValueItem(
    RESULTS_ANNOUNCEMENT_KEY
  );
  const drawerProperties = moduleSettings.properties[0];
  const validationSchema = (_a = AKAMAI_ZIP_CODE_LOCALE_VALIDATION_CONFIGURATION[locale]) == null ? void 0 : _a.validationSchema;
  const preferredLocation = useLocation(
    drawerProperties.defaultLocation.latitude,
    drawerProperties.defaultLocation.longitude,
    validationSchema,
    !!(stwPostalCode && drawerType === "CMW"),
    setStwErrorMessage
  );
  const processWarehouses = useCallback(
    (fetchedWarehouses) => {
      let merged = [...fetchedWarehouses];
      const preferredWarehouse = getPreferredWarehouse(
        warehouse.nearestWarehouse
      );
      const preferred = fetchedWarehouses.find(
        (w) => w.warehouseId === preferredWarehouse
      );
      if (preferred && !isNullOrEmpty(preferred)) {
        if (state.preferredWarehouseData !== preferred) {
          setState((prevState) => ({
            ...prevState,
            preferredWarehouseData: preferred
          }));
        }
      } else if (state.preferredWarehouseData) {
        const exists = fetchedWarehouses.some(
          (w) => {
            var _a2;
            return w.warehouseId === ((_a2 = state.preferredWarehouseData) == null ? void 0 : _a2.warehouseId);
          }
        );
        if (!exists) {
          merged = [
            state.preferredWarehouseData,
            ...fetchedWarehouses
          ];
        }
      }
      return merged;
    },
    [state.preferredWarehouseData, warehouse == null ? void 0 : warehouse.nearestWarehouse]
  );
  const loadWarehouses = useCallback(
    async ({ filters, location }) => {
      setState((prevState) => ({ ...prevState, isLoading: true }));
      if (!agent) return;
      if (!location.latitude || !location.longitude) {
        const displayedWarehouses = processWarehouses([]);
        setState((prevState) => ({
          ...prevState,
          announcement: "",
          isLoading: false,
          warehouses: displayedWarehouses
        }));
        setWarehouses && setWarehouses(displayedWarehouses);
        return;
      }
      try {
        const preferredWarehouse = getPreferredWarehouse(
          warehouse.nearestWarehouse
        );
        const formattedFilters = filters ? getWarehouseFilterParams(
          drawerOptionsDetails,
          filters,
          "warehouseLocatorMobileApi"
        ) : void 0;
        const sortedWarehouses = await getSortedWarehouses({
          agent,
          filters: formattedFilters,
          locale,
          location: {
            latitude: location.latitude,
            longitude: location.longitude
          },
          preferredWarehouse
        });
        const displayedWarehouses = processWarehouses(sortedWarehouses);
        setState((prevState) => ({
          ...prevState,
          announcement: resultsAnnouncement,
          isLoading: false,
          warehouses: displayedWarehouses
        }));
        setWarehouses && setWarehouses(displayedWarehouses);
      } catch {
        const displayedWarehouses = processWarehouses([]);
        setState((prevState) => ({
          ...prevState,
          announcement: "",
          isLoading: false,
          warehouses: displayedWarehouses
        }));
        setWarehouses && setWarehouses(displayedWarehouses);
      }
    },
    [
      agent,
      drawerOptionsDetails,
      locale,
      resultsAnnouncement,
      warehouse,
      processWarehouses,
      setWarehouses
    ]
  );
  const onSearch = useCallback(
    async (searchCriteria) => {
      const getAddressLocation = async (q) => {
        setState((prevState) => ({
          ...prevState,
          announcement: "",
          isLoading: true
        }));
        if (!agent) {
          const displayedWarehouses = processWarehouses([]);
          setState((prevState) => ({
            ...prevState,
            announcement: "",
            isLoading: false,
            warehouses: displayedWarehouses
          }));
          return;
        }
        try {
          const results = await agent.getBingLocation(q);
          const { resources } = results.resourceSets[0];
          const resource = resources.find(
            (element) => element.confidence === "High"
          );
          return {
            latitude: resource == null ? void 0 : resource.point.coordinates[0],
            longitude: resource == null ? void 0 : resource.point.coordinates[1]
          };
        } catch {
          return void 0;
        }
      };
      const location = await getAddressLocation(
        searchCriteria.searchText || ""
      );
      loadWarehouses({
        filters: searchCriteria.filters,
        location: { ...location }
      });
    },
    [agent, loadWarehouses, processWarehouses]
  );
  const getAutosuggest = useCallback(
    async (q) => {
      if (!agent) return;
      const ul = preferredLocation ? `${preferredLocation.latitude},${preferredLocation.longitude}` : "";
      const country = getCountryFromLocale(locale);
      return agent == null ? void 0 : agent.getBingAutosuggest(q, ul, country);
    },
    [agent, locale, preferredLocation]
  );
  const loadWarehousesRef = useRef(loadWarehouses);
  loadWarehousesRef.current = loadWarehouses;
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      announcement: "",
      isLoading: true
    }));
    isOpen && preferredLocation && loadWarehousesRef.current({ location: preferredLocation });
  }, [isOpen, preferredLocation]);
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      isLoading: !!prevState.preferredWarehouse,
      preferredWarehouse: getPreferredWarehouse(
        warehouse.nearestWarehouse
      )
    }));
  }, [warehouse.nearestWarehouse, (_b = warehouse.nearestWarehouse) == null ? void 0 : _b.catalog]);
  return /* @__PURE__ */ jsx(
    WarehouseDrawerUI,
    {
      ...drawerType === "CMW" ? { ...props, stwErrorMessage } : { ...props },
      announcement: state.announcement,
      getAutosuggest,
      isLoading: state.isLoading,
      isOpen: !!isOpen,
      locale,
      onClose: onClose || (() => {
      }),
      onSearch,
      onUpdatePreferredWarehouse: updatePreferredWarehouse,
      preferredWarehouse: state.preferredWarehouse,
      warehouses: state.warehouses
    }
  );
};
WarehouseDrawer.displayName = COMPONENT_NAME;
export {
  WarehouseDrawer
};
